import React, { useEffect, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import { Download, Trash2, RefreshCw, Search, FileText, Users2 } from "lucide-react";
import { T } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";

const MODULE_TONE = {
  listings: "bg-indigo-50 text-indigo-800 border-indigo-200",
  orders: "bg-emerald-50 text-emerald-800 border-emerald-200",
  procurement: "bg-amber-50 text-amber-900 border-amber-300",
  reports: "bg-blue-50 text-blue-800 border-blue-200",
};

function ago(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function bytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function Downloads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [mineOnly, setMineOnly] = useState(!isAdmin);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (moduleFilter) params.module = moduleFilter;
      if (mineOnly) params.mine_only = true;
      const { data } = await api.get("/downloads", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [moduleFilter, mineOnly]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Delete this download record?")) return;
    try {
      await api.delete(`/downloads/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (r) => {
    try {
      await downloadFile(`/downloads/${r.id}/file`, r.filename);
      toast.success(`Downloaded ${r.filename}`);
    } catch (e) {
      toast.error(e?.message || "Download failed");
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.filename || "").toLowerCase().includes(s)
      || (r.module || "").toLowerCase().includes(s)
      || (r.requested_by_email || "").toLowerCase().includes(s)
      || (r.requested_by_name || "").toLowerCase().includes(s);
  });

  const modules = Array.from(new Set(rows.map((r) => r.module).filter(Boolean)));

  return (
    <>
      <PageHeader
        title="Downloads"
        subtitle="Every export ever generated · file, who requested it, when"
        testId={T.moduleTitle("downloads")}
        module="downloads"
      >
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <button
              onClick={() => setMineOnly((m) => !m)}
              data-testid="downloads-mine-toggle"
              className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${mineOnly ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              title={mineOnly ? "Showing only your downloads" : "Showing all users"}
            >
              <Users2 size={12} /> {mineOnly ? "Mine only" : "All users"}
            </button>
          )}
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            data-testid="downloads-module-filter"
            className="h-8 px-2 text-[11.5px] border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All modules</option>
            {modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid={T.searchInput("downloads")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search filename, user…"
              className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <Button onClick={load} testId="downloads-refresh" disabled={loading}>
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        </div>
      </PageHeader>
      <PageBody>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="erp-table w-full" data-testid="downloads-grid">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr>
                  <th style={{ minWidth: 200 }}>File</th>
                  <th style={{ width: 110 }}>Module</th>
                  <th style={{ width: 80 }} className="text-right">Size</th>
                  <th style={{ minWidth: 160 }}>Requested by</th>
                  <th style={{ width: 130 }}>When</th>
                  <th style={{ width: 200 }}>Details</th>
                  <th style={{ width: 130 }} className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`download-row-${r.id}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                    <td className="text-[12px] font-medium text-slate-800">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={13} className="text-slate-400 shrink-0" />
                        <span className="truncate" title={r.filename}>{r.filename}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`inline-flex items-center h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${MODULE_TONE[r.module] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {r.module || "—"}
                      </span>
                    </td>
                    <td className="text-right font-mono text-[11.5px] text-slate-600">{bytes(r.size)}</td>
                    <td className="text-[12px] text-slate-800">
                      <div className="truncate" title={r.requested_by_email}>{r.requested_by_name || r.requested_by_email || "—"}</div>
                      {r.requested_by_email && r.requested_by_name && (
                        <div className="text-[10.5px] text-slate-500 truncate">{r.requested_by_email}</div>
                      )}
                    </td>
                    <td className="font-mono text-[11px] text-slate-600" title={r.created_at}>{ago(r.created_at)}</td>
                    <td className="text-[11px] text-slate-600 max-w-[200px]">
                      {r.meta?.marketplace && <span className="capitalize font-semibold">{r.meta.marketplace}</span>}
                      {r.meta?.kind && <span className="ml-1 text-slate-400">· {r.meta.kind}</span>}
                      {r.meta?.row_count != null && <span className="ml-1 text-slate-400">· {r.meta.row_count} rows</span>}
                      {r.meta?.listing_ids && <span className="ml-1 text-slate-400">· {r.meta.listing_ids.length} listing{r.meta.listing_ids.length === 1 ? "" : "s"}</span>}
                      {r.storage_error && <span className="text-red-700 font-semibold">storage failed</span>}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => download(r)}
                        data-testid={`download-btn-${r.id}`}
                        disabled={!!r.storage_error || !r.storage_path}
                        className="h-6 px-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        title={r.storage_error ? `Unavailable: ${r.storage_error}` : "Download file"}
                      >
                        <Download size={11} /> Download
                      </button>
                      <button
                        onClick={() => remove(r.id)}
                        data-testid={`download-delete-${r.id}`}
                        className="ml-1 h-6 px-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-red-700 hover:bg-red-50 border border-red-200 rounded-sm"
                        title="Delete record"
                      >
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={7} className="p-10 text-center text-slate-500 text-[13px] border-t border-slate-100">
                      {rows.length === 0
                        ? "No downloads yet. Every export you generate from Listings, Orders, etc. will appear here."
                        : "No records match the current filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-2 text-[11.5px] text-slate-600 font-mono">
          {filtered.length} record{filtered.length === 1 ? "" : "s"}
        </div>
      </PageBody>
    </>
  );
}
